/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';

import DropdownItem from './DropdownItem';
import SubMenu from './SubMenuItem';
import doesNodeContainClick from '../Utils/doesNodeContainClick';

const DropdownList = (props) => {
  const {
    children,
    classPrefix,
    right,
    onClose,
  } = props;
  const prefix = classPrefix || 'dropdown';
  const classMenu = cns('dropdown__list', `${prefix}--menu`, right ? `${prefix}--menu--right` : '');

  return (
    children && (
      <ul
        className={classMenu}
        role="menu"
        onClick={onClose}
      >
        {children}
      </ul>
    )
  );
};

DropdownList.propTypes = {
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Indica un prefisso da applicare alle classi css del componente */
  classPrefix: PropTypes.string,
  /** On close callback */
  onClose: PropTypes.func,
  /** Visualizza l'elenco a destra */
  right: PropTypes.bool,
};

DropdownList.defaultProps = {
  children: null,
  classPrefix: '',
  onClose: null,
  right: false,
};

const DropdownComponent = (props) => {
  const {
    open, disabled, toggleComponent, title, onClick,
  } = props;

  return toggleComponent
    ? React.cloneElement(toggleComponent, {
      disabled,
      onClick,
    })
    : (
      <div
        className="dropdown__toggle"
        role="presentation"
        onClick={disabled ? null : onClick}
      >
        {title}
        <span className={open ? 'dropdown__caret--close' : 'dropdown__caret--open'} />
      </div>
    );
};

DropdownComponent.propTypes = {
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Indica se mostrare il menu aperto */
  open: PropTypes.bool,
  /** Etichetta da visualizzare nel campo che renderizza il componente */
  title: PropTypes.string,
  /** Specifica che componente usare come toggle del dropdown */
  toggleComponent: PropTypes.element,
  /** On click callback */
  onClick: PropTypes.func,
};

DropdownComponent.defaultProps = {
  open: false,
  disabled: false,
  title: '',
  toggleComponent: null,
  onClick: null,
};

const Dropdown = (props) => {
  const {
    open: openDefault,
    children,
    title,
  } = props;
  const {
    disabled,
    classPrefix,
    className,
    toggleComponent,
    right,
    onClick,
    ...others
  } = props;

  const [open, setOpen] = useState(openDefault);
  const refNode = useRef();

  useEffect(() => {
    if (!open) return undefined;
    const closeIfClickOutside = (e) => {
      // console.log('closeIfClickOutside called');
      if (!doesNodeContainClick(refNode.current, e)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', closeIfClickOutside);
    // console.log('registered');
    return () => document.removeEventListener('click', closeIfClickOutside);
  }, [open, setOpen]);

  const toggleMenu = useCallback((evt) => {
    setOpen(!open);
    onClick && onClick(!open, evt);
  }, [onClick, open]);

  const chiudi = useCallback((e) => {
    if (e.target.hasAttribute('data-clickable') && e.target.getAttribute('data-clickable') === 'false') return;
    setOpen(false);
  }, [setOpen]);

  const classes = cns(
    'dropdown',
    {
      'dropdown--disabled': disabled,
    },
    open ? 'dropdown--open' : 'dropdown--close',
    classPrefix,
    className,
  );

  return (
    <div
      className={classes}
      ref={refNode}
      {...others}
    >
      <DropdownComponent
        open={open}
        disabled={disabled}
        toggleComponent={toggleComponent}
        title={title}
        onClick={toggleMenu}
      />
      {open
        && (
          <DropdownList classPrefix={classPrefix} right={right} onClose={chiudi}>
            {children}
          </DropdownList>
        )}
    </div>
  );
};

Dropdown.propTypes = {
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica un prefisso da applicare alle classi css del componente */
  classPrefix: PropTypes.string,
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Indica se mostrare inizialmente il menu aperto */
  open: PropTypes.bool,
  /** Indica se aprire verso sinistra la tendina, utile se a bordo schermo */
  right: PropTypes.bool,
  /** Etichetta da visualizzare nel campo che renderizza il componente */
  title: PropTypes.string,
  /** Specifica che componente usare come toggle del dropdown */
  toggleComponent: PropTypes.element,
  /**
   * Callaback invocata al click del toggle component.
   * Il primo prametro specifica se il dropdown è open (true/false), il secondo è l'evento.
   */
  onClick: PropTypes.func,
};

Dropdown.defaultProps = {
  open: false,
  children: null,
  className: '',
  classPrefix: '',
  disabled: false,
  right: false,
  title: '',
  toggleComponent: null,
  onClick: null,
};

Dropdown.Item = DropdownItem;
Dropdown.SubMenu = SubMenu;

export default Dropdown;
